import { getRouteApi } from "@tanstack/react-router";
import { ctaDesc, ctaTitle } from "@/lib/seo";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import {
  ArrowRight,
  Phone,
  Check,
  Sparkles,
  Target,
  ShieldCheck,
  ChevronRight,
  MapPin,
  MessageCircle,
  BadgeCheck,
  Clock,
} from "lucide-react";
import { LeadFormModal } from "@/components/LeadFormModal";
import { speakableSchema } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import {
  getIndustryBySlug,
  INDUSTRY_SOLUTIONS,
  type IndustrySolution,
} from "@/data/industry-solutions";
import { SERVICES, type ServiceId } from "@/data/services";
import { SERVICE_HEAD, type ServiceHeadMeta } from "@/data/services-head.gen";
import { getLocation, locations, type Location } from "@/data/locations";
import { BUSINESS_HUB_LOCATIONS } from "@/routes/industries.$slug.$service.$location";

const routeApi = getRouteApi("/industries/$slug/$service/$location");

export function IndustryServiceLocationPage() {
  const {
    ind,
    service: sHead,
    loc,
  } = routeApi.useLoaderData() as {
    ind: IndustrySolution;
    service: ServiceHeadMeta;
    loc: Location;
  };
  const s = SERVICES[sHead.id];
  const matched = ind.solutions.find((sol) => sol.serviceId === s.id);
  const nearby = BUSINESS_HUB_LOCATIONS.filter(
    (l) => l.slug !== loc.slug && l.region === loc.region,
  ).slice(0, 6);
  const otherServices = ind.relatedServiceIds.filter((id) => id !== s.id);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <nav className="border-b border-white/5 bg-[#0a1a3d] px-4 py-3 text-xs">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-1.5 text-white/80">
          <Link to="/" className="hover:text-amber-300">
            หน้าแรก
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/industries" className="hover:text-amber-300">
            Industries
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/industries/$slug" params={{ slug: ind.slug }} className="hover:text-amber-300">
            {ind.name}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link
            to="/industries/$slug/$service"
            params={{ slug: ind.slug, service: s.id }}
            className="hover:text-amber-300"
          >
            {s.nameTh}
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-amber-300">{loc.nameTh}</span>
        </div>
      </nav>

      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14 sm:py-20">
        <div className="mx-auto max-w-5xl text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-semibold text-amber-200">
            <Sparkles className="h-3 w-3" /> {ind.tagline} · {loc.nameEn}
          </div>
          <h1 className="aeo-speakable mt-4 text-3xl font-extrabold leading-tight sm:text-5xl">
            {s.nameTh} สำหรับ {ind.name} <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              ในพื้นที่ {loc.nameTh}
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-3xl text-base text-white/80 sm:text-lg">
            {matched?.desc ?? s.taglineTh} — ออกแบบเฉพาะสำหรับ {ind.audience} ที่ตั้งอยู่ใน
            {loc.nameTh}
            {loc.description ? ` · ${loc.description}` : ""}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
                >
                  ขอใบเสนอราคาเฉพาะพื้นที่ · ฟรี <ArrowRight className="h-4 w-4" />
                </button>
              }
            />
            <a
              href="https://line.me/ti/p/@nycli"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-bold text-white hover:bg-emerald-400"
            >
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
          <dl className="mt-8 grid gap-3 sm:grid-cols-4">
            <Stat label="เริ่มต้น" value={`฿${s.priceFrom.toLocaleString()}`} />
            <Stat label="ระยะเวลา" value={s.turnaround} />
            <Stat label="พื้นที่บริการ" value={loc.nameTh} />
            <Stat label="ภาษา" value={`${s.languagesCount}+`} />
          </dl>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-amber-400 bg-amber-500/[0.08] p-5 text-[14px] leading-relaxed text-white/90 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-200">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC Legal & Notary Service (DBD 0435567000061) ให้บริการ{s.nameTh}สำหรับลูกค้า{ind.name}
          ในพื้นที่{loc.nameTh} — เริ่ม ฿{s.priceFrom.toLocaleString()} · เสร็จภายใน {s.turnaround}{" "}
          · Account Manager เฉพาะ sector
          {loc.region === "กรุงเทพมหานคร"
            ? " · Walk-in สำนักงานใหญ่ลาดพร้าว 95 หรือนัดพบในออฟฟิศของท่าน"
            : ` · บริการรับ-ส่งเอกสารถึง${loc.nameTh}`}
        </blockquote>

        <section className="mt-12">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
            ทำไม {ind.name} ใน {loc.nameTh} ต้องใช้ {s.nameTh}
          </div>
          <h2 className="aeo-speakable mt-2 text-2xl font-extrabold sm:text-3xl">
            ปัญหาที่เราแก้ให้
          </h2>
          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {ind.painPoints.slice(0, 4).map((p, i) => (
              <div key={i} className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
                <div className="flex items-start gap-3">
                  <Target className="mt-0.5 h-5 w-5 flex-shrink-0 text-rose-300" />
                  <div>
                    <div className="text-sm font-bold text-white">{p.title}</div>
                    <p className="mt-1 text-sm leading-relaxed text-white/70">{p.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-2xl border border-white/10 bg-[#0d2150] p-6 sm:p-8">
          <h2 className="aeo-speakable text-2xl font-extrabold sm:text-3xl">สิ่งที่คุณจะได้รับ</h2>
          <div className="mt-5 grid gap-3 md:grid-cols-2">
            {s.purposesTh.slice(0, 6).map((p, i) => (
              <div
                key={i}
                className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3.5"
              >
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-emerald-300" />
                <span className="text-sm text-white/85">{p}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-500/[0.06] p-4">
            <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-300" />
            <p className="text-sm text-white/85">
              <span className="font-bold text-amber-200">สำหรับลูกค้า {loc.nameTh}:</span>{" "}
              ทีมรับ-ส่งเอกสารถึงออฟฟิศของท่าน · Notary นัดพบในพื้นที่ · ใบเสนอราคาภายใน 15 นาทีผ่าน
              LINE @nycli
            </p>
          </div>
        </section>

        <section className="mt-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">ราคาอ้างอิง · {loc.nameTh}</h2>
          <p className="mt-2 text-sm text-white/70">
            ราคาขั้นต่ำ — ลูกค้า {ind.name} ที่ใช้ retainer ลดสูงสุด 25% · ฟรีจัดส่ง EMS ใน
            {loc.region}
          </p>
          <div className="mt-6 overflow-hidden rounded-2xl border border-white/10">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.06] text-left text-xs uppercase tracking-wider text-white/80">
                <tr>
                  <th className="px-4 py-3">รายการ</th>
                  <th className="px-4 py-3 text-right">ราคา</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {s.prices.slice(0, 6).map((row, i) => (
                  <tr key={i} className="bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-white">{row.item}</div>
                      {row.note && <div className="mt-0.5 text-xs text-white/80">{row.note}</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-amber-200">{row.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-12 rounded-3xl border border-amber-400/20 bg-gradient-to-br from-amber-500/[0.08] to-white/[0.02] p-8 sm:p-12">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-300">
            Industry Case · {ind.name}
          </div>
          <h2 className="mt-2 text-2xl font-extrabold sm:text-3xl">{ind.caseStudy.client}</h2>
          <div className="mt-6 grid gap-6 md:grid-cols-2">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-rose-300">
                Challenge
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.challenge}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                Result
              </p>
              <p className="mt-1.5 text-sm text-white/80">{ind.caseStudy.result}</p>
            </div>
          </div>
          <div className="mt-6 rounded-xl border border-amber-400/30 bg-amber-500/[0.08] p-4 text-center">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">Impact</p>
            <p className="mt-1 text-lg font-extrabold text-amber-100">{ind.caseStudy.metric}</p>
          </div>
        </section>

        {nearby.length > 0 && (
          <section className="mt-14 border-t border-white/10 pt-10">
            <h2 className="text-2xl font-extrabold sm:text-3xl">
              พื้นที่ใกล้เคียง · {s.nameTh} สำหรับ {ind.name}
            </h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {nearby.map((n) => (
                <Link
                  key={n.slug}
                  to="/industries/$slug/$service/$location"
                  params={{ slug: ind.slug, service: s.id, location: n.slug }}
                  className="group rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/50 hover:bg-white/[0.07]"
                >
                  <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                    {n.region}
                  </div>
                  <div className="mt-1 text-sm font-bold text-white group-hover:text-amber-200">
                    {s.nameTh} · {n.nameTh}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300">
                    ดู Pack <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <section className="mt-14 border-t border-white/10 pt-10">
          <h2 className="text-xl font-extrabold sm:text-2xl">
            บริการอื่นใน {ind.name} · {loc.nameTh}
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {otherServices.map((id) => {
              const o = SERVICES[id];
              if (!o) return null;
              return (
                <Link
                  key={id}
                  to="/industries/$slug/$service/$location"
                  params={{ slug: ind.slug, service: id, location: loc.slug }}
                  className="group rounded-xl border border-white/10 bg-white/[0.04] p-4 transition hover:border-amber-400/50 hover:bg-white/[0.07]"
                >
                  <div className="text-sm font-bold text-white group-hover:text-amber-200">
                    {o.nameTh}
                  </div>
                  <div className="mt-1 text-[11px] text-white/80">
                    ฿{o.priceFrom.toLocaleString()} · {o.turnaround}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-amber-300">
                    ดู Pack <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </section>

        <section className="mt-14 rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center sm:p-12">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            พร้อมเริ่ม {s.nameTh} ใน {loc.nameTh}?
          </h2>
          <p className="mt-3 text-white/75">
            Dedicated Account Manager · SLA 24 ชม. · ใบเสนอราคาภายใน 15 นาที
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <LeadFormModal
              trigger={
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
                >
                  ขอใบเสนอราคา · ฟรี
                </button>
              }
            />
            <a
              href="tel:+66832494999"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
          <div className="mt-4 inline-flex items-center gap-2 text-[12px] text-white/80">
            <Clock className="h-3.5 w-3.5" /> ตอบกลับภายใน 15 นาที (Mo-Sa 09:00-18:00)
            <ShieldCheck className="ml-2 h-3.5 w-3.5" /> รับรองถูกต้องตามกฎหมาย
          </div>
        </section>
      </div>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-300">{label}</div>
      <div className="mt-1 text-base font-extrabold text-white sm:text-lg">{value}</div>
    </div>
  );
}
